import { useEffect, useState } from "react";
import { isAgentUpdateConfirmed, isSupersededAgentUpdate, updateRequiresMaintenance, type AgentRelease, type Device } from "./api";
import { displayLocale } from "./localization";

const updateSteps = [
  ["requested", "Updateauftrag übermittelt"],
  ["manifest", "Manifest und Signatur prüfen"],
  ["downloading", "Agent und Systemkomponenten laden und prüfen"],
  ["verified", "Release geprüft · Aktivierung vorbereiten"],
  ["installing", "Neuen Agenten aktivieren und Heartbeat abwarten"],
  ["healthy", "Agent-Version und Gesundheit bestätigt"],
] as const;

const stateIndex = {
	requested: 0,
	downloading: 2,
	verified: 3,
	installing: 4,
	healthy: 5,
	superseded: 5,
  rolled_back: 5,
  failed: 1,
};

const failureStep: Record<string, number> = {
  signature_invalid: 1,
  manifest_invalid: 1,
  replay_blocked: 3,
  size_mismatch: 2,
  hash_mismatch: 2,
  installer_size_mismatch: 2,
  installer_hash_mismatch: 2,
  version_mismatch: 3,
  maintenance_required: 3,
  local_install_error: 4,
  health_check_failed: 5,
};

const errorMessages: Record<string, string> = {
  signature_invalid: "Die Freigabesignatur des Release-Manifests ist ungültig.",
  manifest_invalid: "Das Release-Manifest wurde vom installierten Updater abgelehnt.",
  replay_blocked: "Das Release wurde als Downgrade oder wiederholte Release-Sequenz blockiert.",
  size_mismatch: "Die Größe der Agent-Datei stimmt nicht mit dem signierten Manifest überein.",
  hash_mismatch: "Die Prüfsumme der Agent-Datei stimmt nicht mit dem signierten Manifest überein.",
  installer_size_mismatch: "Die Größe einer Systemkomponente stimmt nicht mit dem Manifest überein.",
  installer_hash_mismatch: "Die Prüfsumme einer Systemkomponente stimmt nicht mit dem Manifest überein.",
  version_mismatch: "Die heruntergeladene Binärdatei meldet eine unerwartete Version.",
  maintenance_required: "Die Systemkomponenten unterscheiden sich. Eine einmalige Wartungsinstallation ist erforderlich.",
  local_install_error: "Beim lokalen Aktivieren des neuen Agenten ist ein Fehler aufgetreten.",
  health_check_failed: "Der neue Agent wurde nicht gesund und die vorherige Version wurde wieder aktiviert.",
  interrupted_update: "Der Versionswechsel wurde unterbrochen. Die vorherige Version wurde wieder aktiviert.",
};

export function AgentUpdateProgress({ device, release }: { device: Device; release: AgentRelease | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 10000);
    return () => window.clearInterval(timer);
  }, []);
  const update = device.agent_update;
  if (!update) {
    return <p className="installation-result">Für dieses Gerät wurde noch kein Agent-Update angefordert.</p>;
  }
  if (update.state === "superseded" || isSupersededAgentUpdate(device)) {
    return <p className="installation-result">Der frühere Auftrag auf Agent {update.desired_version} wurde durch die neuere Installation {device.status?.agent_version} ersetzt.</p>;
  }

  const confirmed = isAgentUpdateConfirmed(device);
  const maintenance = !confirmed && updateRequiresMaintenance(device, release?.version === update.desired_version ? release : null);
  const failed = update.state === "failed" || update.state === "rolled_back";
  const pending = !failed && !maintenance && !confirmed;
  const lastReport = Date.parse(update.reported_at ?? update.requested_at ?? "");
  const waiting = pending && (!Number.isFinite(lastReport) || now - lastReport > Math.max(180000, (device.heartbeat?.interval_s ?? 60) * 3000));
  const offline = pending && !device.status?.online;
  const progress = failed ? (failureStep[update.error_code ?? ""] ?? 1) : update.state === "healthy" && !confirmed ? 4 : (stateIndex[update.state] ?? 0);
  const activeIndex = Math.max(0, Math.min(updateSteps.length - 1, progress));
	const message = maintenance
    ? "Die geschützten Systemkomponenten dieses Agent-Releases unterscheiden sich von der installierten Version. Das normale Agent-Update ändert diese Dateien absichtlich nicht. Bitte einmalig „Neu installieren“ wählen."
		: errorMessages[update.error_code ?? ""] ?? "Das Update konnte nicht abgeschlossen werden.";
	const heading = maintenance ? "Wartungsinstallation erforderlich" : confirmed ? "Erfolgreich" : failed ? (update.state === "rolled_back" ? "Zurückgerollt" : "Fehlgeschlagen") : offline ? "Gerät offline · Rückmeldung ausstehend" : waiting ? "Weitere Rückmeldung ausstehend" : "Update läuft";
	const installedVersion = device.status?.agent_version ?? "unbekannt";
	const offeredVersion = release?.version && release.version !== update.desired_version ? ` · Aktuell angeboten: ${release.version}` : "";

  return <div className={`installation-status agent-update-status ${failed ? "failed" : confirmed ? "healthy" : "pending"}`} role="status" aria-live="polite">
    <div className="installation-status-heading">
      <div>
        <strong>{heading}</strong>
		<span>Installiert: Agent {installedVersion} · Updateziel: {update.desired_version}{offeredVersion}</span>
      </div>
      <b>{failed ? `Stopp bei ${activeIndex + 1}/${updateSteps.length}` : confirmed ? "Fertig" : `Schritt ${activeIndex + 1}/${updateSteps.length}`}</b>
    </div>
    <progress aria-label="Updatefortschritt" aria-valuetext={confirmed ? "Abgeschlossen" : `${activeIndex} von ${updateSteps.length} Schritten abgeschlossen`} max={updateSteps.length} value={confirmed ? updateSteps.length : activeIndex} />
    <ol className="installation-steps" aria-label="Updateschritte">
      {updateSteps.map(([, label], index) => {
        const done = confirmed || index < activeIndex;
        const active = index === activeIndex && !confirmed;
        return <li key={label} className={active && failed ? "failed" : active ? "active" : done ? "done" : "pending"}><i aria-hidden="true" /><span>{label}</span></li>;
      })}
    </ol>
    {/* A maintenance gate is not a failure, so it never reached this block: the
        heading said "Wartungsinstallation erforderlich" while the only sentence
        explaining what to do stayed hidden behind the failure branch. */}
    {(failed || maintenance) && <div className="installation-error"><strong>{message}</strong>{failed && update.error_code && <span>Fehlercode: {update.error_code}</span>}</div>}
	{confirmed && <p className="installation-result">Agent {installedVersion} wurde durch einen authentifizierten Heartbeat erfolgreich bestätigt. Extensions melden ihren Zustand separat.</p>}
    {pending && update.state === "verified" && <p className="installation-result">Signatur und Release-Dateien sind geprüft. Die Aktivierung ist noch nicht bestätigt. Ältere Updater können hier zusätzlich auf die Extension-Installation warten.</p>}
    {pending && update.state === "healthy" && <p className="installation-result">Der Updater meldet Abschluss; die passende Agent-Version und Heartbeat-Bestätigung werden noch abgeglichen.</p>}
    {(waiting || offline) && <p className="installation-result">Angezeigt wird der letzte gemeldete Schritt. Der tatsächliche Fortschritt ist derzeit nicht bestätigt; die Anzeige aktualisiert sich automatisch.</p>}
    <small>
      {update.requested_at ? `Angefordert: ${new Date(update.requested_at).toLocaleString(displayLocale)}` : "Update angefordert"}
      {update.reported_at ? ` · Letzte Rückmeldung: ${new Date(update.reported_at).toLocaleString(displayLocale)}` : " · Rückmeldung des Pi ausstehend"}
    </small>
  </div>;
}
